import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import atlassianIntelligenceIconUrl from 'assets/images/atlassian-intelligence-icon.svg';
import { useIntl } from 'react-intl-next';
import { useRouter } from 'react-resource-router';
import { createSelector } from 'react-sweet-state';
import { getAccountId } from 'state/selectors/user';
import { useConversation } from 'unified-help-center-alpha/src/store/converse';
import { SEARCH_INPUT_FONT_SIZE } from 'view/common/styled';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import * as transitions from 'view/styles/transitions';
import { searchTextbox } from 'view/styles/z-index';
import Button from '@atlaskit/button';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { UnstyledButton } from '@atlassian/help-center-common-component/unstyled-button';
import { ConversationExperience } from '../../conversation';
import messages from './messages';

interface Props {
    accountId: string;
}

const UnifiedHelpSearchTextBox = (props: Props) => {
    const [isChoiceOpen, setIsChoiceOpen] = useState(false);
    const [isUHExperienceSelected, setIsUHExperienceSelected] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [{ location }, { push }] = useRouter();
    const { formatMessage } = useIntl();
    const [, { startNewConversationWithAiMate }] = useConversation();

    const openChoicesOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;
        setSearchQuery(newQuery);
        newQuery !== '' ? setIsChoiceOpen(true) : setIsChoiceOpen(false);
    }, []);

    const redirectToAllResults = useCallback(() => {
        push(`${location.pathname}?q=${searchQuery}`) as unknown as void;
    }, [location.pathname, searchQuery, push]);

    const redirectToUHExperience = useCallback(() => {
        startNewConversationWithAiMate(searchQuery, { accountId: props.accountId });
        setIsUHExperienceSelected(true);
    }, [searchQuery, props.accountId, startNewConversationWithAiMate]);

    return (
        <>
            {!isUHExperienceSelected && (
                <Stack>
                    <Box xcss={searchBoxStyles} id={'new-search-textbox'}>
                        <Input
                            type="text"
                            autoComplete="off"
                            spellCheck={false}
                            placeholder={formatMessage(messages.inputPlaceholder)}
                            onChange={openChoicesOnChange}
                        />

                        <IconButton type="submit" actionSubjectId="search">
                            <SearchIcon primaryColor={token('color.icon', 'gray')} label={'search-button'} />
                        </IconButton>
                    </Box>
                    {isChoiceOpen && (
                        <Box xcss={dropdownStyles}>
                            <Stack grow="fill">
                                {/* eslint-disable-next-line styled-components-a11y/click-events-have-key-events */}
                                <DropDownCard
                                    aria-label="button"
                                    onClick={redirectToUHExperience}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <img src={atlassianIntelligenceIconUrl} alt="" />
                                    <QueryText>{searchQuery}</QueryText>
                                    <Button appearance="subtle-link">
                                        {formatMessage(messages.askAtlassianIntelligence)}
                                    </Button>
                                </DropDownCard>
                                {/* eslint-disable-next-line styled-components-a11y/click-events-have-key-events */}
                                <DropDownCard
                                    aria-label="button"
                                    onClick={redirectToAllResults}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <SearchIcon label="search-icon" size="medium" />
                                    <QueryText>{searchQuery}</QueryText>
                                    <Button appearance="subtle-link">{formatMessage(messages.showAllResults)}</Button>
                                </DropDownCard>
                            </Stack>
                        </Box>
                    )}
                </Stack>
            )}
            {isUHExperienceSelected && (
                <SmartCardProvider>
                    <ConversationExperience />
                </SmartCardProvider>
            )}
        </>
    );
};

const selector = createSelector(getAccountId, (accountId) => ({ accountId }));

export default connect(selector, {})(UnifiedHelpSearchTextBox);

const Input = styled.input`
    ${SEARCH_INPUT_FONT_SIZE};
    ${mixins.placeholder(token('color.text.subtlest', 'gray'))};
    border-radius: 5px;
    padding: ${grid.multiple(2.125).px};
    padding-right: ${token('space.400', '32px')};
    width: 100%;
    border: 0;
    outline: none;
    position: relative;
    z-index: ${searchTextbox};
`;

const IconButton = styled(UnstyledButton)`
    position: absolute;
    padding: 0 ${token('space.200', '16px')};
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    border-radius: 0 ${mixins.borderRadiusValue} ${mixins.borderRadiusValue} 0;
    cursor: pointer;
    z-index: ${searchTextbox};

    &:hover {
        > span {
            ${transitions.zoom()};
        }
    }
`;

const DropDownCard = styled.div`
    display: flex;
    flex-direction: row;
    height: ${token('space.600', '50px')};
    width: '100%';
    justify-content: space-between;
    align-items: center;
    padding: ${token('space.150', '10px')};
    cursor: pointer;

    &:hover {
        background: ${token('color.background.selected.hovered', colors.N0)};
    }
`;

const QueryText = styled.div`
    flex-grow: 1;
    margin-left: ${token('space.150', '10px')};
`;

const searchBoxStyles = xcss({
    position: 'relative',
});

const dropdownStyles = xcss({
    display: 'flex',
    borderRadius: 'border.radius.100',
    background: 'white',
    marginTop: token('space.300', '24px'),
});
